import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Plane, Grid3x3, Crosshair, Landmark } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/status";
import { KpiRing } from "@/components/KpiRing";
import { SCurveChart } from "@/components/SCurveChart";

const KPIS = [
  { key: "akuisisi", label: "Akuisisi Foto Lidar", color: "#0EA5E9", unit: "misi" },
  { key: "cp", label: "Akuisisi CP", color: "#14B8A6", unit: "titik" },
  { key: "foto", label: "Proses Foto", color: "#06B6D4", unit: "misi" },
  { key: "lidar", label: "Proses LiDAR", color: "#6366F1", unit: "misi" },
  { key: "m2d", label: "Modeling 2D", color: "#F59E0B", unit: "grid" },
  { key: "m3d", label: "Modeling 3D", color: "#10B981", unit: "grid" },
];

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 rounded-md border border-border bg-[#131B29] px-4 py-3">
    <Icon className="h-4 w-4 text-sky-400" />
    <span className="text-xs text-slate-400">{label}</span>
    <span className="ml-auto font-display text-2xl font-bold" data-testid={testId}>{value}</span>
  </div>
);

// Card "Bobot Tahapan": satu baris per komponen realisasi. Bar-nya adalah persen komponen itu
// sendiri; kolom kontribusi = bobot × persen / 100, dan jumlahnya sama dengan Progres Keseluruhan.
const BobotTahapan = ({ komponen, overall }) => (
  <div className="rise rounded-md border border-border bg-[#131B29] p-5 sm:p-6" style={{ animationDelay: "500ms" }} data-testid="komponen-card">
    <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
      <div>
        <h3 className="font-display text-2xl font-bold">Bobot Tahapan</h3>
        <p className="text-xs text-slate-400">Kontribusi tiap komponen terhadap progres keseluruhan</p>
      </div>
      <p className="font-mono text-xs text-slate-400">Total: <span className="text-emerald-300" data-testid="komponen-total">{overall}%</span></p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
            <th className="py-2 text-left font-normal">Komponen</th>
            <th className="py-2 text-right font-normal">Bobot</th>
            <th className="py-2 text-right font-normal">Progres</th>
            <th className="w-1/3 py-2 text-left font-normal">Kontribusi</th>
          </tr>
        </thead>
        <tbody>
          {komponen.map((k) => {
            const kontribusi = (k.bobot * k.persen) / 100;
            return (
              <tr key={k.kode} className="border-t border-border/60" data-testid={`komponen-row-${k.kode}`}>
                <td className="py-2 pr-3 text-slate-200">{k.nama}</td>
                <td className="py-2 text-right font-mono text-slate-400" data-testid={`komponen-${k.kode}-bobot`}>{k.bobot}%</td>
                <td className="py-2 text-right font-mono text-slate-300" data-testid={`komponen-${k.kode}-persen`}>{k.persen}%</td>
                <td className="py-2 pl-4">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-[#1C273B]">
                      <div className="h-full rounded-full bg-sky-400" style={{ width: `${k.persen}%` }} />
                    </div>
                    <span className="w-14 text-right font-mono text-xs text-slate-400">{kontribusi.toFixed(2)}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

export default function DashboardPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/summary").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  if (!d) return <div className="p-10 text-slate-500" data-testid="dashboard-loading">Memuat dashboard...</div>;
  // `typeof` dulu: `null >= 0` bernilai true di JS, jadi tanpa guard ini kartu "belum ada data"
  // tampil hijau berpanah naik — persis kebalikan dari artinya.
  const ahead = typeof d.deviasi === "number" && d.deviasi >= 0;
  const adaPerbandingan = typeof d.deviasi === "number";

  return (
    <div className="p-4 sm:p-6 lg:p-10 grid-bg min-h-screen" data-testid="dashboard-page">
      <div className="rise flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400">Modul 04 · Dashboard</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-1">Ringkasan Progres</h2>
          <p className="text-sm text-slate-400 mt-2">Periode proyek {fmtDate(d.project.start)} — {fmtDate(d.project.end)} · rencana &amp; deviasi per {fmtDate(d.project.deviasi_per || d.project.today)}</p>
          {/* Dua tanggal di halaman ini dan itu disengaja: deviasi menilai data hari yang sudah
              lengkap, sedangkan progres berjalan menampilkan angka terkini. Tanpa baris ini,
              tanggal di atas terbaca sebagai tanggal seluruh dashboard. */}
          <p className="text-xs text-slate-500 mt-1">Progres berjalan per {fmtDate(d.project.today)}</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded-md border border-border bg-[#131B29] px-5 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Progres Keseluruhan</p>
            <p className="font-display text-4xl font-extrabold text-emerald-400" data-testid="overall-progress">{d.overall}%</p>
          </div>
          <div className="rounded-md border border-border bg-[#131B29] px-5 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
              Deviasi vs Rencana{d.project.deviasi_per ? ` (per ${fmtDate(d.project.deviasi_per)})` : ""}
            </p>
            <p className={`font-display text-4xl font-extrabold flex items-center gap-1 ${adaPerbandingan ? (ahead ? "text-emerald-400" : "text-rose-400") : "text-slate-400"}`} data-testid="deviation-value">
              {adaPerbandingan ? (
                <>
                  {ahead ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}{ahead ? "+" : ""}{d.deviasi}%
                </>
              ) : "—"}
            </p>
            {!adaPerbandingan && <p className="text-[10px] text-slate-500">data kemarin belum tersedia</p>}
          </div>
        </div>
      </div>

      {/* Enam dalam satu baris; isi kartunya mengecil di xl supaya ring dan angkanya tetap
          berdampingan, bukan ditumpuk atas-bawah (lihat KpiRing). */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {KPIS.map((k, i) => <KpiRing key={k.key} label={`Persentase ${k.label}`} data={d.stages[k.key]} color={k.color} unit={k.unit} testId={`kpi-${k.key}`} delay={i * 70} />)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
        <div className="rise rounded-md border border-border bg-[#131B29] p-5 sm:p-6" style={{ animationDelay: "350ms" }}>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 className="font-display text-2xl font-bold">Kurva S · Rencana vs Realisasi</h3>
              <p className="text-xs text-slate-400">Progres kumulatif harian seluruh tahapan (berbobot)</p>
            </div>
            <p className="font-mono text-xs text-slate-400">Rencana saat ini: <span className="text-sky-300">{d.rencana_saat_ini}%</span></p>
          </div>
          <SCurveChart data={d.kurva_s} />
        </div>
        <div className="rise space-y-3" style={{ animationDelay: "420ms" }}>
          <Stat icon={Plane} label="Flight Plan" value={d.counts.flights} testId="count-flights" />
          <Stat icon={Grid3x3} label="Grid Peta" value={d.counts.grids} testId="count-grids" />
          <Stat icon={Crosshair} label="Control Points" value={d.counts.cp} testId="count-cp" />
          <Stat icon={Landmark} label="Titik BM" value={d.counts.bm} testId="count-bm" />
        </div>
      </div>

      {/* 13 baris tidak muat di kolom kanan 320px, jadi card ini lebar penuh di bawah grafik. */}
      <div className="mt-6">
        <BobotTahapan komponen={d.komponen} overall={d.overall} />
      </div>
    </div>
  );
}
